<template>
  <div class="root">
    <Alert
        v-show="notification.message"
        :message="notification.message"
        :type="notification.type"
        ref="alertComponent"
        class="margin-top-10"
    />
    <div class="hh-profile-container padding-top-40">
      <div class="container-fluid">
        <form action="#" @submit.prevent="submitForm" class="form" ref="form" v-if="dataAnalysed">

          <div class="hh-padlock-wrapper">
            <div class="hh-lock-capsule" v-if="!isUnlocked" v-longclick="processLongClick">
              <div v-show="!unLocking" class="hh-circle-icon"/>
              <loading-animation v-show="unLocking" :height="33" :width="33" type="spinner"/>
              <span class="hh-lock-text">Click and hold to unlock</span>
            </div>
            <div v-longclick="processLongClick" class="margin-right-15">
              <img v-if="isUnlocked" class="hh-padlock" src="/dist/site/static/icons/padlock-unlocked.svg"
                   alt="Unlocked Icon">
              <img v-if="!isUnlocked" :class="unLocking ? 'hh-padlock-continuous-shake' : 'hh-padlock-default-shake'"
                   class="hh-padlock" src="/dist/site/static/icons/padlock-locked.svg" alt="Locked Icon">
            </div>
          </div>

          <div slot="title" class="row">
            <div class="col-sm-12">
              <h3 class="text-center form-title">Final step!</h3>
            </div>
            <div class="col-sm-12">
              <p class="text-center">
                Add bank information to start receiving donations
              </p>
            </div>
          </div>

          <div class="section-body padding-bottom-50">
            <div class="row margin-top-20">
              <div class="col-sm-12">
                <div class="payout-information-section" ref="payoutInformation">
                  <PayoutInformation
                      ref="payoutInformationComponent"
                      :user-data="userData"
                      :static-data="staticData"
                      :userRoles="userRoles"
                      :errors="this.payoutInformationErrors"
                      :edit-mode="isUnlocked"
                      :show-non-editable="true"
                      :show-title="true"
                      :show-edit="false"
                      @setPayoutFormState="setPayoutFormState"
                      @componentLoading="componentLoading"
                  />

                </div>
              </div>
            </div>

            <div class="row margin-bottom-30">
              <div class="col-xs-12">
                <button class="btn hh-btn hh-btn-green btn-responsive-small width-100" type="submit" name="Save and publish campaign">
                  Save and Publish Campaign
                </button>
              </div>
            </div>

            <div class="row back-row" @click="backAction">
              <div class="col-md-12 text-center margin-bottom-20 padding-bottom-10">
                <img src="/dist/site/static/icons/back.svg" alt="Back Icon">
                <span class="hh-back-btn">Go Back</span>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <confirm-bank-information-modal
        ref="confirmBankInformation"
        :userCampaigns="userCampaigns"
        @complete="isUnlocked = true"
    />
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import InputLabel from '../form/elements/InputLabel'
import CurrencyInput from '../form/elements/CurrencyInput'
import DatePicker from '../form/elements/DatePicker'
import TextEditor from '../form/elements/TextEditor'
import Modal from "../form/elements/Modal"
import PayoutInformation from '../form/PayoutInformation'
import vSelect from 'vue-select'
import MultipleImageUpload from '../form/MultipleImageUpload'
import ImageUpload from '../form/ImageUpload'
import Alert from '../form/elements/Alert'
import LoadingAnimation from '../LoadingAnimation'
import ConfirmBankInformationModal from '../campaign/ConfirmBankInformationModal'
import Form from '../../plugins/hh-form.js'
import {scrollTo} from '../../utils/componentHelper'

export default {
  name: 'VerifyBankInformation',

  components: {
    PayoutInformation,
    ConfirmBankInformationModal,
    CurrencyInput,
    InputLabel,
    DatePicker,
    vSelect,
    TextEditor,
    MultipleImageUpload,
    ImageUpload,
    Modal,
    Alert,
    LoadingAnimation,
  },

  props: {
    campaignData: Object,
    userCampaigns: Object,
    alias: String,
  },

  data() {
    return {
      loading: true,
      longClickEvents: 0,
      dataAnalysed: false,
      showTooltip: true,
      forceRecompute: 0,
      campaignSlug: null,
      componentsLoadingStatus: {
        PayoutInformation: true,
      },
      notification: {
        message: null,
        type: null,
      },
      isPayoutFormReady: true,
      config: {},
      userData: {
        first_name: null,
        last_name: null,
        date_of_birth: null,
        business_name: null,
        business_type: null,
        url: null,
        identification_number: null,
        account_number: null,
        routing_number: null,
        informationType: 'PayoutInformation',
      },
      staticData: {},
      userRoles: [],
      isUnlocked: true,
      form: new Form({
        publish: true,
        step: 5,
      }, this.$api),
    }
  },

  beforeMount() {
    this.form.campaignSlug = this.campaignSlug = this.campaignData.slug

    if (_.isEmpty(this.profileData)) {
      this.fetchData().then(() => {
        this.analyseUserData()
      })
    } else {
      this.analyseUserData()
    }

  },

  mounted() {
    if (!_.isEmpty(this.alert)) {
      this.notification = this.alert
      this.$store.commit("setAlert", null)
    }

    setTimeout(() => {
      this.showTooltip = false
    }, 3000)
  },

  methods: {
    ...mapActions('campaign', ['publishCampaign']),
    ...mapActions('profile', ['fetchData', 'storeInformation']),

    analyseUserData() {
      this.dataAnalysed = false
      this.loading = true

      if (!_.isEmpty(this.profileData)) {
        this.userRoles = this.profileData.data.attributes.roles

        this.fillUserData()
        this.fillConfigData()
        this.fillStaticData()
      }

      this.dataAnalysed = true
      this.loading = false
    },

    fillUserData() {
      if (!_.isEmpty(this.profileData.data.relations.payoutInformation)) {
        let payoutInformation = this.profileData.data.relations.payoutInformation.data.attributes

        this.userData = {...this.userData, ...payoutInformation}
        this.userData.business_name = this.profileData.data.attributes.business_name
        this.isUnlocked = false
      }
    },

    fillStaticData() {
      if (!_.isEmpty(this.profileData.data.staticData)) {
        this.staticData = this.profileData.data.staticData
      }
    },

    fillConfigData() {
      if (!_.isEmpty(this.profileData.config)) {
        this.config = this.profileData.config
      }
    },

    setPayoutFormState(value) {
      this.isPayoutFormReady = value
    },

    cancelAction() {
      window.location = '/dashboard'
    },

    backAction() {
      this.$emit('navigateBack')
    },

    componentLoading(componentName, value) {
      this.componentsLoadingStatus[componentName] = value
    },

    closeModal() {
      this.isUnlocked = false
      this.$refs.confirmBankInformation.closeModal()
    },

    submitForm() {
      this.loading = true

      if (!this.isUnlocked) {
        return this.requestPublishCampaign()
      }

      if (!this.processPayoutInformation()) {
        return false
      }

      return this.submitBankInformation()
    },

    processPayoutInformation() {
      if (!this.$refs.payoutInformationComponent.canSubmit()) {
        this.loading = false
        scrollTo(this.$refs['form'])
        return false
      }

      return true
    },

    submitBankInformation() {
      this.storeInformation(this.$refs.payoutInformationComponent.$data.formData).then(() => {
        if (this.payoutInformationStatus === 'success') {
          this.$refs.alertComponent.reactivate()
          this.notification = {
            message:
                'Bank information updated.',
            type: "bg-success",
          }
          this.loading = false
          this.requestPublishCampaign()
        } else {
          this.loading = false
        }
      })
    },

    async requestPublishCampaign() {
      this.form.campaignSlug = this.campaignSlug

      await this.publishCampaign(this.form)
      this.forceRecompute++

      if (this.form.errors.any()) {
        this.loading = false
        scrollTo(this.$refs['form'])
        let errorMessage = 'Something went wrong. Please try again later.'

        if (this.form.errors.has('publish')) {
          errorMessage = this.form.errors.get('publish')
        }

        this.$refs.alertComponent.reactivate()
        this.notification = {
          message:
          errorMessage,
          type: "bg-danger",
        }

        return false
      }

      this.$emit('formSubmitted')
      this.loading = false
    },

    processLongClick() {
      if (this.longClickEvents > 30) {
        this.longClickEvents = -1
        this.$refs.confirmBankInformation.openModal()

      } else {
        this.longClickEvents += 1
        let currentClickEvents = this.longClickEvents
        setTimeout(() => {
          if (this.longClickEvents === currentClickEvents) {
            this.longClickEvents = 0
          }
        }, 300)
      }
    },
  },

  computed: {
    ...mapGetters('profile', ['profileData']),
    ...mapGetters(['alert']),

    storeInformationData: {
      get() {
        return this.$store.getters['campaign/storeInformationData'](this.campaignSlug, this.formData.informationType, this.forceRecompute)
      },
    },

    status: {
      get() {
        return this.$store.getters['campaign/storeInformationStatus'](this.campaignSlug, this.formData.informationType, this.forceRecompute)
      },
    },

    payoutInformationStatus: {
      get() {
        return this.$store.getters['profile/storeInformationStatus'](this.userData.informationType)
      },
    },

    payoutInformationErrors: {
      get() {
        return this.$store.getters['profile/storeInformationErrors'](this.userData.informationType)
      },
    },

    errors: {
      get() {
        return this.$store.getters['campaign/storeInformationErrors'](this.campaignSlug, this.formData.informationType, this.forceRecompute)
      },
    },

    isFormReady() {
      return !this.loading && this.isPayoutFormReady
    },

    isLoading() {
      for (let component in this.componentsLoadingStatus) {
        if (this.componentsLoadingStatus[component]) {
          return true
        }
      }

      return this.loading && this.dataAnalysed
    },

    available_business_types() {
      let result = []

      for (const [key, value] of Object.entries(this.staticData.available_business_types)) {
        result.push({label: value, code: key})
      }

      return result
    },

    unLocking() {
      return this.longClickEvents > 1
    }
  },

  watch: {
    isLoading(to, from) {
      let componentName = this.$options.name
      if (this.alias) {
        componentName = this.alias
      }
      this.$emit('componentLoading', componentName, to)
    },
  }
}
</script>

<style lang="scss" scoped>
hr {
  border: 1px solid whitesmoke;
}

.hh-btn-green {
  padding: 20px;
  width: 100%;
}

.form-title {
  color: black;
}
</style>
